import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  type OnChanges,
  type SimpleChanges,
} from '@angular/core';
import { OnlineIndicatorSize } from '@freelancer/ui/online-indicator';
import { FontColor, TextSize, TextVerticalAlign } from '@freelancer/ui/text';
import { LocalizedDateFns } from '../localized-date-fns.service';
import { ActivityStatusState } from './user-activity-status.types';
import { OnlineIndicatorComponent } from '../online-indicator/online-indicator.component';
import { RelativeTimeComponent } from '../relative-time/relative-time.component';

@Component({
  selector: 'fl-user-activity-status',
  standalone: true,
  imports: [OnlineIndicatorComponent, RelativeTimeComponent],
  template: `
    @if (currentState === ActivityStatusState.ONLINE) {
      <fl-online-indicator
        [border]="true"
        [isOnline]="true"
        [setWidth]="true"
        [size]="size"
        [sizeTablet]="sizeTablet"
        [sizeDesktop]="sizeDesktop"
        [username]="username"
      ></fl-online-indicator>
    }

    <div
      class="LastActiveStatus"
      [class.LastActiveStatus-hidden]="
        disableLastActiveTimestampStatus ||
        currentState !== ActivityStatusState.RECENTLY_OFFLINE
      "
      [attr.data-is-recent]="isRecent"
      [attr.aria-label]="ariaLabel"
    >
      @if (lastActiveDate) {
        <fl-relative-time
          [color]="FontColor.INHERIT"
          [date]="lastActiveDate"
          [roundJustNowString]="true"
          [includeSeconds]="false"
          [includeSpace]="true"
          [roundingMethod]="'floor'"
          [shortFormat]="true"
          [strict]="true"
          [size]="TextSize.XXXSMALL"
          [verticalAlign]="TextVerticalAlign.MIDDLE"
        >
        </fl-relative-time>
      }
    </div>
  `,
  styleUrls: ['./user-activity-status.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserActivityStatusComponent implements OnChanges {
  ActivityStatusState = ActivityStatusState;
  FontColor = FontColor;
  OnlineIndicatorSize = OnlineIndicatorSize;
  TextSize = TextSize;
  TextVerticalAlign = TextVerticalAlign;

  private cd = inject(ChangeDetectorRef);
  private dateFns = inject(LocalizedDateFns);

  /** Always hide the last active timestamp status */
  @Input() disableLastActiveTimestampStatus?: boolean = false;
  @Input() lastActiveTimestamp?: number;
  @Input({ required: true }) isOnline: boolean;
  @Input() size: OnlineIndicatorSize = OnlineIndicatorSize.ACTIVITY_STATUS;
  @Input() sizeTablet?: OnlineIndicatorSize;
  @Input() sizeDesktop?: OnlineIndicatorSize;
  @Input() username?: string;

  ariaLabel: string;

  lastActiveDate: Date | undefined;
  currentState: ActivityStatusState | undefined;
  isRecent = false;
  now: Date | undefined;

  private async updateAriaLabel(): Promise<void> {
    if (this.currentState !== ActivityStatusState.RECENTLY_OFFLINE) {
      return;
    }

    if (!this.lastActiveDate) {
      this.ariaLabel = $localize`${this.username || 'User'} is offline.`;
      return;
    }

    const lastActive = await this.dateFns.formatDistanceStrict(
      this.lastActiveDate,
      this.now ?? new Date(),
      {
        addSuffix: true,
        roundingMethod: 'floor',
        shortFormat: false,
      },
    );
    this.ariaLabel = $localize`${this.username || 'User'} was last online ${lastActive}.`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.username) {
      this.updateAriaLabel();
    }

    if (changes.lastActiveTimestamp || changes.isOnline) {
      if (!this.isOnline && this.lastActiveTimestamp) {
        this.lastActiveDate = new Date(this.lastActiveTimestamp);
        this.calculateView();
      } else if (this.isOnline) {
        this.currentState = ActivityStatusState.ONLINE;
      } else {
        this.currentState = ActivityStatusState.OFFLINE;
      }
    }
  }

  calculateView(): void {
    if (!this.lastActiveTimestamp || this.isOnline) {
      return;
    }

    this.now = new Date();
    const difference = this.now.getTime() - this.lastActiveTimestamp;

    // 24 hours
    if (difference >= 1000 * 60 * 60 * 24) {
      this.currentState = ActivityStatusState.OFFLINE;
    } else {
      this.currentState = ActivityStatusState.RECENTLY_OFFLINE;
    }

    // 1 hour
    if (difference < 60 * 60 * 1000) {
      this.isRecent = true;
    } else {
      this.isRecent = false;
    }

    this.updateAriaLabel();
    this.cd.markForCheck();
  }
}
